import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronRight,
  CircleHelp,
  Clock,
  Headphones,
  Mail,
  MessageCircle,
  Phone,
  Send,
  TriangleAlert,
  MessageSquare,
} from "lucide-react";
import { ORDER_STATUS } from "@evocha/shared-types";

type TicketStatus = "open" | "waiting" | "resolved";

interface Ticket {
  id: string;
  subject: string;
  category: string;
  status: TicketStatus;
  updated: string;
  preview: string;
}

const TICKETS: Ticket[] = [
  { id: "SUP-1042", subject: "Sale EVCH-4M8X2A still pending", category: "Transaction", status: "waiting", updated: "38 min ago", preview: "We are checking with the network. Please do not repeat the sale." },
  { id: "SUP-1039", subject: "Limit increase for weekend volume", category: "Account", status: "open", updated: "Yesterday", preview: "Your request is with the risk team. We expect an answer by Friday." },
  { id: "SUP-1021", subject: "Top-up not showing on wallet", category: "Wallet", status: "resolved", updated: "12 Oct", preview: "The M-Pesa top-up was confirmed and credited. Reference MP-88213." },
];

const STATUS_META: Record<TicketStatus, { label: string; badge: string }> = {
  open: { label: "Open", badge: "ev-badge-blue" },
  waiting: { label: "Waiting on us", badge: "ev-badge-pending" },
  resolved: { label: "Resolved", badge: "ev-badge-success" },
};

const CATEGORIES = ["Transaction issue", "Wallet & top-up", "Account & limits", "Odoo / statements", "Something else"];

const FAQS = [
  "A sale is pending — should I repeat it?",
  "How long does a wallet top-up take?",
  "How is my commission calculated?",
  "How do I change my transaction PIN?",
];

export function Support() {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reference, setReference] = useState("");
  const [sent, setSent] = useState(false);
  const [active, setActive] = useState<Ticket | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSent(true);
    window.setTimeout(() => {
      setSent(false);
      setSubject("");
      setMessage("");
      setReference("");
    }, 4800);
  }

  return (
    <div className="stack-6">
      <div className="page-head">
        <div>
          <h2 className="ev-h2">Support</h2>
          <p className="ev-muted">Real people, 07:00 – 22:00 EAT, every day. Median first reply: 4 minutes.</p>
        </div>
        <span className="support-sla"><span className="live-dot" /> Support is online</span>
      </div>

      <div className="support-channels">
        <a className="support-channel" href="https://wa.me/255700000000" target="_blank" rel="noreferrer">
          <span className="support-icon support-wa"><MessageCircle size={20} /></span>
          <span><strong>WhatsApp</strong><em>Fastest · +255 700 000 000</em></span>
          <ArrowRight size={16} />
        </a>
        <a className="support-channel" href="tel:+255700000000">
          <span className="support-icon support-phone"><Phone size={20} /></span>
          <span><strong>Call the agent desk</strong><em>07:00 – 22:00 EAT</em></span>
          <ArrowRight size={16} />
        </a>
        <a className="support-channel" href="mailto:support@skttanzania.co.tz">
          <span className="support-icon support-mail"><Mail size={20} /></span>
          <span><strong>Email</strong><em>support@skttanzania.co.tz</em></span>
          <ArrowRight size={16} />
        </a>
      </div>

      <div className="support-grid">
        <div className="stack-5">
          <section className="ev-card">
            <div className="ev-card-head">
              <div>
                <h3 className="ev-card-title">Your requests</h3>
                <p className="ev-caption">One thread per issue keeps things quick.</p>
              </div>
              <span className="ev-badge ev-badge-neutral">{TICKETS.length}</span>
            </div>
            <div className="ticket-list">
              {TICKETS.map((t) => (
                <button key={t.id} className="ticket" onClick={() => setActive(t)}>
                  <span className={`ticket-mark ticket-${t.status}`}><MessageSquare size={16} /></span>
                  <span className="ticket-main">
                    <span className="ticket-top">
                      <strong>{t.subject}</strong>
                      <span className={`ev-badge ${STATUS_META[t.status].badge}`}>{STATUS_META[t.status].label}</span>
                    </span>
                    <span className="ticket-preview">{t.preview}</span>
                    <span className="ticket-meta">{t.id} · {t.category} · updated {t.updated}</span>
                  </span>
                  <ChevronRight size={16} className="ticket-arrow" />
                </button>
              ))}
            </div>
          </section>

          <section className="ev-card ev-card-pad">
            <div className="ev-row-between">
              <div>
                <span className="ev-eyebrow">Common questions</span>
                <h3 className="ev-h3" style={{ marginTop: 4 }}>Answers before you wait</h3>
              </div>
              <CircleHelp size={19} className="ev-muted" />
            </div>
            <div className="mini-faq">
              {FAQS.map((q) => (
                <Link key={q} to="/help" className="mini-faq-row">
                  <span>{q}</span>
                  <ChevronRight size={15} />
                </Link>
              ))}
            </div>
            <Link to="/help" className="ev-btn ev-btn-sm ev-btn-secondary ev-btn-block">Open the help centre</Link>
          </section>
        </div>

        <aside className="stack-5">
          <section className="ev-card ev-card-pad">
            <div className="section-head">
              <div>
                <span className="ev-eyebrow">New request</span>
                <h3 className="ev-h3" style={{ marginTop: 4 }}>Tell us what happened</h3>
              </div>
              <Headphones size={19} className="ev-muted" />
            </div>

            <form className="support-form" onSubmit={submit}>
              <label className="ev-field">
                <span className="ev-label">What is it about?</span>
                <select className="ev-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>

              <label className="ev-field">
                <span className="ev-label">Transaction reference <em className="ev-muted" style={{ fontStyle: "normal", fontWeight: 400 }}>(optional)</em></span>
                <input className="ev-input ev-mono" value={reference} onChange={(e) => setReference(e.target.value.toUpperCase())} placeholder="EVCH-XXXXXX" />
              </label>

              <label className="ev-field">
                <span className="ev-label">Subject</span>
                <input className="ev-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="In one line, what is wrong?" />
              </label>

              <label className="ev-field">
                <span className="ev-label">Details</span>
                <textarea className="ev-textarea" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="What did you expect, and what happened instead? Include times if you can." />
              </label>

              <button className="ev-btn ev-btn-primary ev-btn-block" disabled={!subject.trim() || !message.trim()}>
                <Send size={16} /> Send request
              </button>
              <p className="ev-hint">Never share your PIN or password. evocha staff will never ask for them.</p>
            </form>

            {sent && (
              <div className="inline-success" role="status">
                <Check size={15} />
                <span>Request received. We will reply by SMS and email. Demo mode does not create a real ticket.</span>
              </div>
            )}
          </section>

          <section className="ev-card ev-card-pad urgent-card">
            <span className="urgent-icon"><TriangleAlert size={18} /></span>
            <div>
              <strong>Suspected fraud or a missing payout?</strong>
              <p>Call the agent desk immediately. Do not share your PIN, even with someone claiming to be from evocha.</p>
              <a href="tel:+255700000000" className="ev-btn ev-btn-sm ev-btn-danger"><Phone size={14} /> Call now</a>
            </div>
          </section>

          <div className="support-hours">
            <Clock size={16} />
            <div><strong>Today’s hours</strong><span>07:00 – 22:00 EAT · Dar es Salaam</span></div>
          </div>
        </aside>
      </div>

      {active && (
        <div className="modal-scrim" role="presentation" onClick={() => setActive(null)}>
          <div className="ticket-modal" role="dialog" aria-modal="true" aria-label={active.subject} onClick={(e) => e.stopPropagation()}>
            <div className="ev-row-between">
              <span className={`ev-badge ${STATUS_META[active.status].badge}`}>{STATUS_META[active.status].label}</span>
              <button className="ev-btn ev-btn-sm ev-btn-ghost" onClick={() => setActive(null)}>Close</button>
            </div>
            <h3 className="ev-h3" style={{ marginTop: 14 }}>{active.subject}</h3>
            <p className="ev-caption">{active.id} · {active.category} · updated {active.updated}</p>
            <div className="ticket-thread">
              <div className="thread-msg thread-agent"><span className="ev-avatar ev-avatar-soft">SK</span><div><strong>evocha support</strong><p>{active.preview}</p><em>{active.updated}</em></div></div>
              <div className="thread-msg"><span className="ev-avatar">AM</span><div><strong>You</strong><p>Thank you — please let me know as soon as there is an update.</p><em>Just now</em></div></div>
            </div>
            <div className="ticket-reply">
              <input className="ev-input" placeholder="Write a reply…" aria-label="Reply" />
              <button className="ev-btn ev-btn-primary"><Send size={16} /></button>
            </div>
            <p className="ev-hint">Demo mode — replies are not sent.</p>
          </div>
        </div>
      )}

      <p className="support-foot">
        For a pending sale, check the status in <Link to="/transactions">transactions</Link> first. Pending means{" "}
        <strong>{ORDER_STATUS.PENDING.label.en.toLowerCase()}</strong> — never repeat it.
      </p>
    </div>
  );
}
